// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2024 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { getIsWebCrawler } from "@insite/client-framework/Common/WebCrawler";
import { HasProduct, withProduct } from "@insite/client-framework/Components/ProductContext";
import { ConfigurationType } from "@insite/client-framework/Services/ProductServiceV2";
import { FulfillmentMethod } from "@insite/client-framework/Services/SessionService";
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import DataTableHead from "@insite/mobius/DataTable/DataTableHead";
import { DataTableBody, DataTableCell, DataTableHeader, DataTableRow, Typography } from "@insite/mobius/index";
import { TypographyPresentationProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import Notify from "@sc/App/Components/Notify";
import DataTable from "@sc/MobiusOverrides/DataTable/DataTable";
import StyledBox, { StyledBoxStyles } from "@sc/XDL/Components/Common/Box";
import { avoidBreakInsidePrintElement, parseBoolean, safeJsonParse } from "@sc/XDL/Utilities";
import * as React from "react";
import { connect } from "react-redux";
import { css } from "styled-components";

type Props = WidgetProps & HasProduct & ReturnType<typeof mapStateToProps>;

const mapStateToProps = (state: ApplicationState) => ({
    configurationCompleted: state.pages.productDetails.configurationCompleted,
    includeRealTimeInventoryAndPricingForSeo:
        getSettingsCollection(state).websiteSettings.includeRealTimeInventoryAndPricingForSeo,
    session: state.context.session,
    // XNG-changes: SC-1080
    hideWebInventory: parseBoolean(state.context.session.properties?.hideWebInventory?.toLowerCase()),
});

export interface ProductDetailsWarehouseAvailabilityStyles {
    container?: StyledBoxStyles;
    title?: TypographyPresentationProps;
    notify?: InjectableCss;
}

export const warehouseAvailabilityStyles: ProductDetailsWarehouseAvailabilityStyles = {
    container: {
        container: {
            css: css`
                @media print {
                    margin-bottom: 16px;
                }
                ${avoidBreakInsidePrintElement}
            `,
        },
    },
    notify: {
        css: css`
            margin: 12px 0 0;
        `,
    },
    title: {
        variant: "h4",
        forwardAs: "p",
        css: css`
            margin-bottom: 12px;
        `,
    },
};

const styles = warehouseAvailabilityStyles;

const ProductDetailsWarehouseAvailability = ({
    product,
    configurationCompleted,
    includeRealTimeInventoryAndPricingForSeo,
    productInfo,
    session,
    // XNG-Changes: SC-1080
    hideWebInventory
}: Props) => {
    if (
        !product ||
        (getIsWebCrawler() && !includeRealTimeInventoryAndPricingForSeo) ||
        product.isVariantParent ||
        (!!product.configurationType &&
            product.configurationType !== ConfigurationType.None &&
            product.configurationType !== ConfigurationType.Fixed &&
            !configurationCompleted)
    ) {
        return null;
    }

    const { inventory, unitOfMeasure, failedToLoadInventory } = productInfo;

    const warehousesDtos =
        inventory?.inventoryWarehousesDtos
            ?.find(o => o.unitOfMeasure.toLowerCase() === (unitOfMeasure?.toLowerCase() || ""))
            ?.warehouseDtos?.filter(
                warehouseDto => (warehouseDto as any)?.properties?.isDistributionCenter?.toLowerCase() === "true",
            ) || [];

    const distributionCentersQty = warehousesDtos.reduce((sum, item) => sum + item.qty, 0);

    if (failedToLoadInventory) {
        return null;
    }

    const defaultWarehouseInfo = session?.properties?.warehouseInfo
        ? safeJsonParse(session.properties.warehouseInfo).description
        : "";

    return (
        <StyledBox extendedStyles={styles.container}>
            <Typography {...styles.title}>{translate("Warehouse Availability")}</Typography>
            <DataTable>
                <DataTableHead>
                    <DataTableHeader>{translate("Warehouse Name")}</DataTableHeader>
                    <DataTableHeader>{translate("QTY")}</DataTableHeader>
                </DataTableHead>
                <DataTableBody>
                    {(session?.pickUpWarehouse?.description || defaultWarehouseInfo) && (
                        <DataTableRow>
                            <DataTableCell>
                                {session.fulfillmentMethod === FulfillmentMethod.Ship
                                    ? defaultWarehouseInfo
                                    : session?.pickUpWarehouse?.description}
                            </DataTableCell>
                            <DataTableCell>{Number(inventory?.additionalResults?.qtyInBranch) || 0}</DataTableCell>
                        </DataTableRow>
                    )}
                    {/* XNG-Changes: SC-1080 */}
                    {!hideWebInventory && (
                        <DataTableRow>
                            <DataTableCell>{translate("Distribution Centers")}</DataTableCell>
                            <DataTableCell>{distributionCentersQty}</DataTableCell>
                        </DataTableRow>
                    )}
                </DataTableBody>
            </DataTable>
            <Notify {...styles.notify} type="info">
                {siteMessage("Custom_insufficient_qty_warning")}
            </Notify>
        </StyledBox>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps)(withProduct(ProductDetailsWarehouseAvailability)),
    definition: {
        displayName: "Warehouse Availability Table",
        group: "Product Details",
        allowedContexts: ["ProductDetailsPage"],
    },
};

export default widgetModule;
